import * as React from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';
import { ArrowRight } from 'lucide-react';
import { Button, type ButtonProps } from './button';

/**
 * Project StickyCta.
 *
 * The floating footer that holds the primary action on the onboarding
 * screens (Figma "Floating CTA"). It sticks to the bottom of the scroll
 * area and fades the page out behind it with a Feather gradient, so long
 * option lists run underneath rather than colliding with the button.
 */
export type StickyCtaProps = BoxProps;

export const StickyCta = ({ children, ...rest }: StickyCtaProps) => (
  <Box
    position="sticky"
    bottom={0}
    zIndex={6}
    w="100%"
    pt="14px"
    pb="16px"
    display="flex"
    flexDirection="column"
    // Tokens cannot be interpolated into a gradient. `--ayble-page-bg` is
    // published from `colors.feather.100` in theme/chakra.ts.
    // The opaque stop is a fixed px distance from the top edge, not a
    // percentage of total height — a percentage assumes a short,
    // single-button footer, and produces visible bleed-through of
    // scrolled content when actionArea holds multiple stacked elements
    // (e.g. a CTA plus a secondary "skip" link) and the footer grows taller.
    background="linear-gradient(to top, var(--ayble-page-bg, #eef4f6) calc(100% - 32px), rgba(238, 244, 246, 0))"
    {...rest}
  >
    {children}
  </Box>
);

/**
 * Project ArrowCta.
 *
 * The primary onboarding action: an Evergreen pill that fills its column,
 * with the label on the left and a Blue disc holding a forward arrow pinned
 * right (prototype `.btn.btn--arrow`).
 *
 * Sizing follows the prototype rather than the Figma "Main CTA" frame, which
 * draws the button as a fixed 200x40 pill. The two sources disagree and the
 * prototype is the layout reference; Figma governs colour and type. Sizing
 * lives here rather than in a Button `size` variant because this shape is
 * specific to the CTA.
 */
export type ArrowCtaProps = ButtonProps;

export const ArrowCta = ({ children, ...rest }: ArrowCtaProps) => (
  <Button
    h="54px"
    minH="54px"
    w="100%"
    pl="26px"
    pr="9px"
    fontSize="15.5px"
    justifyContent="space-between"
    _disabled={{
      bg: 'rgba(4, 51, 56, 0.35)',
      color: 'light.100',
      cursor: 'not-allowed',
      opacity: 1,
      '& [data-cta-arrow]': { bg: 'rgba(184, 227, 253, 0.5)' },
    }}
    {...rest}
  >
    {children}
    <Box
      as="span"
      aria-hidden
      data-cta-arrow=""
      bg="blue.100"
      color="secondary.500"
      rounded="full"
      boxSize="34px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <ArrowRight size={16} strokeWidth={2.5} />
    </Box>
  </Button>
);
